import classNames from "classnames";
import {
  IModifierGroup,
  IModifierOption,
  ModifierGroupCustomization,
} from "../../types";
import { FaSquarePlus, FaSquareMinus } from "react-icons/fa6";
import { twMerge } from "tailwind-merge";
import { formatPrice } from "../../utils/helpers";

interface ModifierOptionProps {
  modifierGroup: IModifierGroup;
  modifierOption: IModifierOption;
  modifierGroupCustomization: ModifierGroupCustomization;
  setModifierGroupCustomization: React.Dispatch<
    React.SetStateAction<ModifierGroupCustomization>
  >;
}

const ModifierOptionWithQty: React.FC<ModifierOptionProps> = ({
  modifierGroup,
  modifierOption,
  modifierGroupCustomization,
  setModifierGroupCustomization,
}) => {
  // Find the index of the modifier option
  const currentCustomizationIndex =
    modifierGroupCustomization.modifierOptions.findIndex(
      (mOption) => mOption._id === modifierOption._id
    );

  // Get the current quantity (default to 0 if not found)
  const currentQty =
    currentCustomizationIndex === -1
      ? 0
      : modifierGroupCustomization.modifierOptions[currentCustomizationIndex]
          .qty;

  const totalGroupQty = modifierGroupCustomization.modifierOptions.reduce(
    (sum, option) => sum + option.qty,
    0
  );

  const isOutOfStock = modifierOption.haveStock === false;

  // Function to increase quantity or select if maxPermitted is 1
  const handleAddQty = () => {
    if (isOutOfStock) return;

    let updatedModifierOptions;

    // If the group allows only 1 item total, selecting a new one should replace the old one
    if (modifierGroup.quantityInfo.maxPermitted === 1) {
      if (currentQty > 0) return; // Already selected this one

      updatedModifierOptions = [
        {
          _id: modifierOption._id,
          qty: 1,
          displayName: modifierOption.displayName,
          price: modifierOption.price,
        },
      ];
    } else if (modifierOption.quantityInfo.maxPermitted === 1) {
      // If this specific option allows only 1, toggle behavior (0 or 1)
      if (currentQty > 0) return; // Already selected, max is 1

      updatedModifierOptions = [
        ...modifierGroupCustomization.modifierOptions,
        {
          _id: modifierOption._id,
          qty: 1,
          displayName: modifierOption.displayName,
          price: modifierOption.price,
        },
      ];
    } else {
      // Standard quantity increase logic
      if (currentCustomizationIndex === -1) {
        updatedModifierOptions = [
          ...modifierGroupCustomization.modifierOptions,
          {
            _id: modifierOption._id,
            qty: 1,
            displayName: modifierOption.displayName,
            price: modifierOption.price,
          },
        ];
      } else {
        updatedModifierOptions = modifierGroupCustomization.modifierOptions.map(
          (mOption, index) =>
            index === currentCustomizationIndex
              ? { ...mOption, qty: mOption.qty + 1 }
              : mOption
        );
      }
    }

    // Update state with new modifier options
    setModifierGroupCustomization({
      ...modifierGroupCustomization,
      modifierOptions: updatedModifierOptions,
    });
  };

  // Function to decrease quantity
  const handleReduceQty = () => {
    if (isOutOfStock) return;
    if (currentCustomizationIndex === -1) return; // If item is not found, do nothing

    const currentQty =
      modifierGroupCustomization.modifierOptions[currentCustomizationIndex].qty;
    let updatedModifierOptions;

    if (currentQty === 1) {
      // If qty is 1, remove the item
      updatedModifierOptions =
        modifierGroupCustomization.modifierOptions.filter(
          (mOption) => mOption._id !== modifierOption._id
        );
    } else {
      // Otherwise, decrease the quantity
      updatedModifierOptions = modifierGroupCustomization.modifierOptions.map(
        (mOption, index) =>
          index === currentCustomizationIndex
            ? { ...mOption, qty: mOption.qty - 1 }
            : mOption
      );
    }

    // Update state
    setModifierGroupCustomization({
      ...modifierGroupCustomization,
      modifierOptions: updatedModifierOptions,
    });
  };

  const baseQtyBtnClasses =
    "w-6 h-6 cursor-pointer text-primary transition-colors";
  const plusBtnClasses = twMerge(
    classNames(baseQtyBtnClasses, {
      "opacity-50 cursor-not-allowed":
        modifierOption.quantityInfo.maxPermitted > 1 &&
        (currentQty >= modifierOption.quantityInfo.maxPermitted ||
          totalGroupQty >= modifierGroup.quantityInfo.maxPermitted),
      "grayscale opacity-20 cursor-not-allowed text-gray-400": isOutOfStock,
    })
  );

  const minusBtnClasses = twMerge(
    classNames(baseQtyBtnClasses, {
      "opacity-50 cursor-not-allowed":
        currentQty === 0 ||
        currentQty <= modifierOption.quantityInfo.minPermitted,
      "grayscale opacity-20 cursor-not-allowed text-gray-400": isOutOfStock,
    })
  );

  // Determine if we should show simple + toggle or +/- qty controls
  const showSimpleToggle = modifierOption.quantityInfo.maxPermitted === 1;

  if (isOutOfStock) {
    return (
      <div className="flex gap-1 p-3 border-b border-gray-100 last:border-0 justify-between items-center bg-gray-50/50 rounded-lg opacity-60 cursor-not-allowed">
        <div className="flex flex-col">
          <div className="font-medium text-gray-400 line-through">
            {modifierOption.displayName}
          </div>
          <div className="text-gray-400 text-sm font-medium">
            (Épuisé)
          </div>
        </div>
        <div className="px-3 py-1 bg-gray-100 text-gray-400 text-xs font-bold rounded-md uppercase tracking-wider cursor-not-allowed">
          Indisponible
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-1 p-3 border-b border-gray-100 last:border-0 justify-between items-center hover:bg-gray-50 transition-colors rounded-lg">
      <div className="flex flex-col">
        <div className="font-medium text-gray-800">
          {modifierOption.displayName}
        </div>
        <div className="text-gray-500 text-sm font-medium">
          {`+${formatPrice(modifierOption.price.value)}`}
        </div>
      </div>

      <div className="flex items-center gap-3">
        {showSimpleToggle ? (
          <div
            onClick={currentQty > 0 ? handleReduceQty : handleAddQty}
            className={classNames(
              "w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200 border",
              currentQty > 0
                ? "bg-primary border-primary text-white"
                : "bg-white border-gray-300 text-primary hover:border-primary"
            )}
          >
            {currentQty > 0 ? (
              <FaSquareMinus className="w-4 h-4" />
            ) : (
              <FaSquarePlus className="w-4 h-4" />
            )}
          </div>
        ) : (
          <>
            <FaSquareMinus
              className={minusBtnClasses}
              onClick={handleReduceQty}
            />
            <div className="w-6 text-center font-semibold text-gray-900">
              {" "}
              {currentQty}{" "}
            </div>
            <FaSquarePlus className={plusBtnClasses} onClick={handleAddQty} />
          </>
        )}
      </div>
    </div>
  );
};

export default ModifierOptionWithQty;
